import React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { Button, ButtonVariant } from '../../common/Button'
import { useModal } from '../../../hooks/useModal'
import { ConfirmationModal } from '../../common/ConfirmationModal'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'
import { theme } from '../../../theme/theme'
import { CreateUpdateRegisterEntranceItemModal } from './CreateUpdateRegisterEntranceItemModal'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { ToastType } from '../../common/Toast/toast-type'
import { Alert } from '../../common/Alert'
import { AlertType } from '../../common/Alert/alert-type'
import { ISocietyEntranceModel } from '../../../interfaces/models/society-entrance.interfaces'
import { unitQueries } from '../../../api/units'
import { deleteEntrance } from '../../../api/society-entrances'
import { i18nSocietyType } from '../../../i18n'

interface RegisterEntranceItemProps {
  entrance: ISocietyEntranceModel
}

export const RegisterEntranceItem = ({
  entrance,
}: RegisterEntranceItemProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { society } = useCurrentSociety()
  const {
    show: showConfirmationModal,
    open: openConfirmationModal,
    close: closeConfirmationModal,
  } = useModal()
  const {
    show: showEditModal,
    open: openEditModal,
    close: closeEditModal,
  } = useModal()

  const { data: societyUnits } = useQuery(
    unitQueries.getUnitsPerSociety(society?._id)
  )

  const entranceUnits =
    societyUnits?.filter((unit) => unit.entranceId === entrance._id) || []

  const showSuccessMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate('registerEntrance.flashMessage.deleteEntranceSuccess', {
        context: i18nSocietyType(society?.societyTypeDisplayName),
      })
    )
  }

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('registerEntrance.flashMessage.deleteEntranceFailure', {
        context: i18nSocietyType(society?.societyTypeDisplayName),
      })
    )
  }

  const { mutate: mutateDeleteEntrance } = useMutation({
    mutationFn: deleteEntrance,
    onSuccess: () => {
      showSuccessMessage()
    },
    onError: () => {
      showErrorMessage()
    },
  })

  const onDeleteEntrance = async (): Promise<void> => {
    mutateDeleteEntrance(entrance._id)
  }

  const entranceUnitsWithoutUnassigned = entranceUnits.filter(
    (unit) => !unit.isUnassigned
  )

  const getUnitText = (): string => {
    if (entranceUnitsWithoutUnassigned.length === 1) {
      return translate('common.singularPlural.unit.singular', {
        context: i18nSocietyType(society?.societyTypeDisplayName),
      })
    }
    return translate('common.singularPlural.unit.plural', {
      context: i18nSocietyType(society?.societyTypeDisplayName),
    })
  }

  const getConnectText = (): string => {
    if (entranceUnitsWithoutUnassigned.length === 1) {
      return translate('common.singularPlural.connected.singular')
    }
    return translate('common.singularPlural.connected.plural')
  }

  return (
    <div>
      <p style={theme.textVariants.base}>
        {translate('registerEntrance.numberOfUnits', {
          number: entranceUnitsWithoutUnassigned.length,
          unitText: getUnitText().toLowerCase(),
          connectText: getConnectText(),
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
      </p>
      <div
        className={`mt-4 space-x-3 ${entranceUnits.length === 0 ? 'mb-2' : ''}`}
      >
        <Button
          label={translate('common.actions.change')}
          onClick={openEditModal}
        />
        <Button
          label={translate('common.actions.delete')}
          variant={ButtonVariant.DANGER}
          disabled={entranceUnits.length > 0}
          onClick={openConfirmationModal}
        />
      </div>
      {entranceUnitsWithoutUnassigned.length > 0 && (
        <p style={theme.textVariants.caption} className="my-2 text-red">
          {translate('registerEntrance.warnings.unableDeleteWithUnits', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          })}
        </p>
      )}
      {entranceUnits.length === 1 &&
        entranceUnitsWithoutUnassigned.length === 0 && (
          <Alert
            className="my-3"
            text={translate(
              'registerEntrance.warnings.unableToDeleteWithUnassigned',
              { context: i18nSocietyType(society?.societyTypeDisplayName) }
            )}
            type={AlertType.WARNING}
          />
        )}
      <ConfirmationModal
        title={translate('registerEntrance.confirmationDialog.deleteTitle', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        description={translate(
          'registerEntrance.confirmationDialog.deleteDescription',
          { context: i18nSocietyType(society?.societyTypeDisplayName) }
        )}
        show={showConfirmationModal}
        close={closeConfirmationModal}
        onConfirm={onDeleteEntrance}
        deleteMode
      />
      <CreateUpdateRegisterEntranceItemModal
        show={showEditModal}
        close={closeEditModal}
        entrance={entrance}
      />
    </div>
  )
}
